//! Log Store Hook - Zustand Store für Logs

import type { LogEntry, LogFilter, LogStoreStats, ScopeFilter } from "../entities";

import { create } from "zustand";
import { devtools } from "zustand/middleware";

interface LogStoreState {
  autoScroll: boolean;
  // Filter
  currentFilter: LogFilter;

  error: null | string;
  filteredLogs: LogEntry[];
  // Real-time
  isLiveMode: boolean;

  // UI State
  isLoading: boolean;

  isOverlayMinimized: boolean;
  // Debug Overlay State
  isOverlayOpen: boolean;

  lastUpdate: Date | null;
  // Logs
  logs: LogEntry[];
  scopeFilter?: ScopeFilter;

  showErrorsOnly: boolean;
  // Statistics
  stats: LogStoreStats | null;
}

interface LogStoreActions {
  addLog: (log: LogEntry) => void;
  clearLogs: () => void;
  // Actions
  refreshLogs: () => Promise<void>;

  refreshStats: () => Promise<void>;
  setError: (error: null | string) => void;
  // Filtering
  setFilter: (filter: LogFilter) => void;

  setLastUpdate: (date: Date) => void;

  // Real-time
  setLiveMode: (live: boolean) => void;
  // UI State
  setLoading: (loading: boolean) => void;

  // Log Management
  setLogs: (logs: LogEntry[]) => void;
  setOverlayMinimized: (minimized: boolean) => void;
  setOverlayOpen: (open: boolean) => void;
  setScopeFilter: (scopeFilter: ScopeFilter | undefined) => void;

  setShowErrorsOnly: (showErrorsOnly: boolean) => void;
  // Statistics
  setStats: (stats: LogStoreStats) => void;

  toggleAutoScroll: () => void;
  // Debug Overlay State
  toggleOverlay: () => void;
}

type LogStore = LogStoreState & LogStoreActions;

const initialState: LogStoreState = {
  autoScroll: true,
  currentFilter: {
    domains: [],
    limit: 1000,
  },
  error: null,
  filteredLogs: [],
  isLiveMode: true,
  isLoading: false,
  isOverlayMinimized: false,
  isOverlayOpen: false,
  lastUpdate: null,
  logs: [],
  showErrorsOnly: false,
  stats: null,
};

/**
 * Log Store Hook
 *
 * Zustand Store für Log-Management mit Filtering und Real-time Updates.
 */
export const useLogStore = create<LogStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      addLog: (log) => {
        set((state) => {
          // Prüfe auf Duplikate basierend auf ID
          const existingLogIndex = state.logs.findIndex((existingLog) => existingLog.id === log.id);
          if (existingLogIndex !== -1) {
            // Log bereits vorhanden, nicht hinzufügen
            return state;
          }

          const newLogs = [log, ...state.logs].slice(0, 1000); // Keep last 1000 logs

          // Apply current filters to the new logs
          const { currentFilter, scopeFilter, showErrorsOnly } = state;
          let filtered = [...newLogs];

          // Apply error-only filter first (highest priority)
          if (showErrorsOnly) {
            filtered = filtered.filter((log) => log.level === "error");
          }

          // Apply level filter
          if (currentFilter.min_level) {
            const levelPriority = {
              debug: 0,
              error: 3,
              info: 1,
              warn: 2,
            };
            const minPriority = levelPriority[currentFilter.min_level];
            filtered = filtered.filter((log) => levelPriority[log.level] >= minPriority);
          }

          // Apply domain filter
          if (currentFilter.domains.length > 0) {
            filtered = filtered.filter((log) => currentFilter.domains.includes(log.domain));
          }

          // Apply scope filter
          if (scopeFilter) {
            filtered = filtered.filter((log) => {
              // Domain filter
              if (
                scopeFilter.domains.length > 0 &&
                !scopeFilter.domains.includes(log.scope.domain)
              ) {
                return false;
              }

              // Module filter
              if (scopeFilter.module && log.scope.module !== scopeFilter.module) {
                return false;
              }

              // File filter
              if (scopeFilter.file && log.scope.file !== scopeFilter.file) {
                return false;
              }

              // Function filter
              if (scopeFilter.function && log.scope.function !== scopeFilter.function) {
                return false;
              }

              return true;
            });
          }

          // Apply message filter
          if (currentFilter.message) {
            const searchTerm = currentFilter.message.toLowerCase();
            filtered = filtered.filter((log) => log.message.toLowerCase().includes(searchTerm));
          }

          // Apply time filter
          if (currentFilter.start_time) {
            const startTime = new Date(currentFilter.start_time);
            filtered = filtered.filter((log) => new Date(log.timestamp) >= startTime);
          }

          if (currentFilter.end_time) {
            const endTime = new Date(currentFilter.end_time);
            filtered = filtered.filter((log) => new Date(log.timestamp) <= endTime);
          }

          // Apply limit
          if (currentFilter.limit) {
            filtered = filtered.slice(0, currentFilter.limit);
          }

          return {
            ...state,
            filteredLogs: filtered,
            lastUpdate: new Date(),
            logs: newLogs,
          };
        });
      },

      clearLogs: () => {
        set({ filteredLogs: [], logs: [] });
      },

      // Actions
      refreshLogs: () => {
        const { setError, setLoading } = get();

        try {
          setLoading(true);
          setError(null);

          // NOTE: LogService.getLogs() call is not yet implemented
          // const response = await LogService.getLogs({ filter: get().currentFilter });
          // setLogs(response.logs);
        } catch (error) {
          setError(error instanceof Error ? error.message : "Unknown error");
        } finally {
          setLoading(false);
        }
        return Promise.resolve();
      },

      refreshStats: () => {
        const { setError, setLoading } = get();

        try {
          setLoading(true);
          setError(null);

          // NOTE: LogService.getStats() call is not yet implemented
          // const stats = await LogService.getStats();
          // setStats(stats);
        } catch (error) {
          setError(error instanceof Error ? error.message : "Unknown error");
        } finally {
          setLoading(false);
        }
        return Promise.resolve();
      },

      setError: (error) => {
        set({ error });
      },

      // Filtering
      setFilter: (filter) => {
        set((state) => {
          const newState = { ...state, currentFilter: filter };
          // Apply filters immediately
          const { logs, scopeFilter, showErrorsOnly } = newState;
          let filtered = [...logs];

          // Apply error-only filter first (highest priority)
          if (showErrorsOnly) {
            filtered = filtered.filter((log) => log.level === "error");
          }

          // Apply level filter
          if (filter.min_level) {
            const levelPriority = {
              debug: 0,
              error: 3,
              info: 1,
              warn: 2,
            };
            const minPriority = levelPriority[filter.min_level];
            filtered = filtered.filter((log) => levelPriority[log.level] >= minPriority);
          }

          // Apply domain filter
          if (filter.domains.length > 0) {
            filtered = filtered.filter((log) => filter.domains.includes(log.domain));
          }

          // Apply scope filter
          if (scopeFilter) {
            filtered = filtered.filter((log) => {
              // Domain filter
              if (
                scopeFilter.domains.length > 0 &&
                !scopeFilter.domains.includes(log.scope.domain)
              ) {
                return false;
              }

              // Module filter
              if (scopeFilter.module && log.scope.module !== scopeFilter.module) {
                return false;
              }

              // File filter
              if (scopeFilter.file && log.scope.file !== scopeFilter.file) {
                return false;
              }

              // Function filter
              if (scopeFilter.function && log.scope.function !== scopeFilter.function) {
                return false;
              }

              return true;
            });
          }

          // Apply message filter
          if (filter.message) {
            const searchTerm = filter.message.toLowerCase();
            filtered = filtered.filter((log) => log.message.toLowerCase().includes(searchTerm));
          }

          // Apply time filter
          if (filter.start_time) {
            const startTime = new Date(filter.start_time);
            filtered = filtered.filter((log) => new Date(log.timestamp) >= startTime);
          }

          if (filter.end_time) {
            const endTime = new Date(filter.end_time);
            filtered = filtered.filter((log) => new Date(log.timestamp) <= endTime);
          }

          // Apply limit
          if (filter.limit) {
            filtered = filtered.slice(0, filter.limit);
          }

          return { ...newState, filteredLogs: filtered };
        });
      },

      setLastUpdate: (lastUpdate) => {
        set({ lastUpdate });
      },

      // Real-time
      setLiveMode: (isLiveMode) => {
        set({ isLiveMode });
      },

      // UI State
      setLoading: (isLoading) => {
        set({ isLoading });
      },

      // Log Management
      setLogs: (logs) => {
        set((state) => {
          // Apply current filters to the new logs
          const { currentFilter, scopeFilter, showErrorsOnly } = state;
          let filtered = [...logs];

          // Apply error-only filter first (highest priority)
          if (showErrorsOnly) {
            filtered = filtered.filter((log) => log.level === "error");
          }

          // Apply level filter
          if (currentFilter.min_level) {
            const levelPriority = {
              debug: 0,
              error: 3,
              info: 1,
              warn: 2,
            };
            const minPriority = levelPriority[currentFilter.min_level];
            filtered = filtered.filter((log) => levelPriority[log.level] >= minPriority);
          }

          // Apply domain filter
          if (currentFilter.domains.length > 0) {
            filtered = filtered.filter((log) => currentFilter.domains.includes(log.domain));
          }

          // Apply scope filter
          if (scopeFilter) {
            filtered = filtered.filter((log) => {
              // Domain filter
              if (
                scopeFilter.domains.length > 0 &&
                !scopeFilter.domains.includes(log.scope.domain)
              ) {
                return false;
              }

              // Module filter
              if (scopeFilter.module && log.scope.module !== scopeFilter.module) {
                return false;
              }

              // File filter
              if (scopeFilter.file && log.scope.file !== scopeFilter.file) {
                return false;
              }

              // Function filter
              if (scopeFilter.function && log.scope.function !== scopeFilter.function) {
                return false;
              }

              return true;
            });
          }

          // Apply message filter
          if (currentFilter.message) {
            const searchTerm = currentFilter.message.toLowerCase();
            filtered = filtered.filter((log) => log.message.toLowerCase().includes(searchTerm));
          }

          // Apply time filter
          if (currentFilter.start_time) {
            const startTime = new Date(currentFilter.start_time);
            filtered = filtered.filter((log) => new Date(log.timestamp) >= startTime);
          }

          if (currentFilter.end_time) {
            const endTime = new Date(currentFilter.end_time);
            filtered = filtered.filter((log) => new Date(log.timestamp) <= endTime);
          }

          // Apply limit
          if (currentFilter.limit) {
            filtered = filtered.slice(0, currentFilter.limit);
          }

          return {
            ...state,
            filteredLogs: filtered,
            lastUpdate: new Date(),
            logs,
          };
        });
      },

      setOverlayMinimized: (isOverlayMinimized) => {
        set({ isOverlayMinimized });
      },

      setOverlayOpen: (isOverlayOpen) => {
        set({ isOverlayOpen });
      },

      setScopeFilter: (scopeFilter) => {
        set((state) => {
          const newState = { ...state };
          if (scopeFilter === undefined) {
            delete newState.scopeFilter;
          } else {
            newState.scopeFilter = scopeFilter;
          }
          // Apply filters immediately
          const { currentFilter, logs, showErrorsOnly } = newState;
          let filtered = [...logs];

          // Apply error-only filter first (highest priority)
          if (showErrorsOnly) {
            filtered = filtered.filter((log) => log.level === "error");
          }

          // Apply level filter
          if (currentFilter.min_level) {
            const levelPriority = {
              debug: 0,
              error: 3,
              info: 1,
              warn: 2,
            };
            const minPriority = levelPriority[currentFilter.min_level];
            filtered = filtered.filter((log) => levelPriority[log.level] >= minPriority);
          }

          // Apply domain filter
          if (currentFilter.domains.length > 0) {
            filtered = filtered.filter((log) => currentFilter.domains.includes(log.domain));
          }

          // Apply scope filter
          if (scopeFilter) {
            filtered = filtered.filter((log) => {
              // Domain filter
              if (
                scopeFilter.domains.length > 0 &&
                !scopeFilter.domains.includes(log.scope.domain)
              ) {
                return false;
              }

              // Module filter
              if (scopeFilter.module && log.scope.module !== scopeFilter.module) {
                return false;
              }

              // File filter
              if (scopeFilter.file && log.scope.file !== scopeFilter.file) {
                return false;
              }

              // Function filter
              if (scopeFilter.function && log.scope.function !== scopeFilter.function) {
                return false;
              }

              return true;
            });
          }

          // Apply message filter
          if (currentFilter.message) {
            const searchTerm = currentFilter.message.toLowerCase();
            filtered = filtered.filter((log) => log.message.toLowerCase().includes(searchTerm));
          }

          // Apply time filter
          if (currentFilter.start_time) {
            const startTime = new Date(currentFilter.start_time);
            filtered = filtered.filter((log) => new Date(log.timestamp) >= startTime);
          }

          if (currentFilter.end_time) {
            const endTime = new Date(currentFilter.end_time);
            filtered = filtered.filter((log) => new Date(log.timestamp) <= endTime);
          }

          // Apply limit
          if (currentFilter.limit) {
            filtered = filtered.slice(0, currentFilter.limit);
          }

          return { ...newState, filteredLogs: filtered };
        });
      },

      setShowErrorsOnly: (showErrorsOnly) => {
        set((state) => {
          const newState = { ...state, showErrorsOnly };
          // Apply filters immediately
          const { currentFilter, logs, scopeFilter } = newState;
          let filtered = [...logs];

          // Apply error-only filter first (highest priority)
          if (showErrorsOnly) {
            filtered = filtered.filter((log) => log.level === "error");
          }

          // Apply level filter
          if (currentFilter.min_level) {
            const levelPriority = {
              debug: 0,
              error: 3,
              info: 1,
              warn: 2,
            };
            const minPriority = levelPriority[currentFilter.min_level];
            filtered = filtered.filter((log) => levelPriority[log.level] >= minPriority);
          }

          // Apply domain filter
          if (currentFilter.domains.length > 0) {
            filtered = filtered.filter((log) => currentFilter.domains.includes(log.domain));
          }

          // Apply scope filter
          if (scopeFilter) {
            filtered = filtered.filter((log) => {
              // Domain filter
              if (
                scopeFilter.domains.length > 0 &&
                !scopeFilter.domains.includes(log.scope.domain)
              ) {
                return false;
              }

              // Module filter
              if (scopeFilter.module && log.scope.module !== scopeFilter.module) {
                return false;
              }

              // File filter
              if (scopeFilter.file && log.scope.file !== scopeFilter.file) {
                return false;
              }

              // Function filter
              if (scopeFilter.function && log.scope.function !== scopeFilter.function) {
                return false;
              }

              return true;
            });
          }

          // Apply message filter
          if (currentFilter.message) {
            const searchTerm = currentFilter.message.toLowerCase();
            filtered = filtered.filter((log) => log.message.toLowerCase().includes(searchTerm));
          }

          // Apply time filter
          if (currentFilter.start_time) {
            const startTime = new Date(currentFilter.start_time);
            filtered = filtered.filter((log) => new Date(log.timestamp) >= startTime);
          }

          if (currentFilter.end_time) {
            const endTime = new Date(currentFilter.end_time);
            filtered = filtered.filter((log) => new Date(log.timestamp) <= endTime);
          }

          // Apply limit
          if (currentFilter.limit) {
            filtered = filtered.slice(0, currentFilter.limit);
          }

          return { ...newState, filteredLogs: filtered };
        });
      },

      // Statistics
      setStats: (stats) => {
        set({ stats });
      },

      toggleAutoScroll: () => {
        set((state) => ({ autoScroll: !state.autoScroll }));
      },

      // Debug Overlay State
      toggleOverlay: () => {
        set((state) => ({
          isOverlayMinimized: false, // Reset minimized when toggling
          isOverlayOpen: !state.isOverlayOpen,
        }));
      },
    }),
    {
      name: "log-store",
    },
  ),
);
